import styled, { keyframes } from 'styled-components';
import backgroundImage from '../../assets/images/background.webp';

// ========================================
// Animations
// ========================================

export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// ========================================
// Main Container
// ========================================

export const PageContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  background: #fff;
  overflow-x: hidden;
  box-sizing: border-box;
`;

export const SEOHelmet = styled.div`
  display: none;
`;

// ========================================
// Container
// ========================================

export const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
  box-sizing: border-box;
  overflow: visible;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }
`;

// ========================================
// Hero Section
// ========================================

export const HeroSection = styled.section`
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 4rem 1.5rem 3rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(30, 76, 92, 0.9) 0%,
      rgba(217, 125, 43, 0.85) 100%
    );
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    min-height: auto;
    padding: 3rem 1rem 2.5rem;
  }
`;

export const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  @media (max-width: 768px) {
    background-attachment: scroll;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
  width: 100%;
  animation: ${fadeInUp} 0.8s ease-out;
`;

export const LaunchBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(217, 125, 43, 0.4);
  animation: ${pulse} 2s ease-in-out infinite;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 1.2rem;
  }
`;

export const HeroImageContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto 2rem;
  border-radius: 20px;
  overflow: hidden;
  animation: ${fadeInScale} 0.8s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 16px;
  }
`;

export const HeroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.03);
  }
`;

export const HeroTitle = styled.h1`
  color: white;
  font-size: clamp(2rem, 6vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 2px 4px 12px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.5px;
  line-height: 1.1;
  animation: ${fadeIn} 0.8s ease-out 0.3s both;
`;

export const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: clamp(1.1rem, 3vw, 1.4rem);
  margin-bottom: 0;
  text-shadow: 1px 2px 8px rgba(0, 0, 0, 0.2);
  font-weight: 400;
  line-height: 1.5;
  animation: ${fadeIn} 0.8s ease-out 0.4s both;
`;

// ========================================
// Pricing Section
// ========================================

export const PricingSection = styled.section`
  padding: 3rem 1.5rem;
  background: white;
  
  @media (max-width: 768px) {
    padding: 2.5rem 1rem;
  }
`;

export const PricingCard = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 24px;
  padding: 2.5rem 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(217, 125, 43, 0.2);
  animation: ${fadeInUp} 0.8s ease-out;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
  }
`;

export const PricingTitle = styled.h2`
  text-align: center;
  font-size: clamp(1.6rem, 4vw, 2rem);
  color: #1E4C5C;
  margin-bottom: 1rem;
  font-weight: 700;
`;

export const PriceOptionsSubtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
  font-weight: 500;
`;

export const PriceOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.25rem;
  }
`;

// Alias for PriceOptions to support both names
export const PriceGrid = PriceOptions;

export const PriceOption = styled.div`
  position: relative;
  padding: 1.5rem;
  border-radius: 16px;
  background: ${props => {
    if (props.$selected) {
      return props.$recommended
        ? 'linear-gradient(135deg, rgba(217, 125, 43, 0.12) 0%, rgba(217, 125, 43, 0.20) 100%)'
        : 'linear-gradient(135deg, rgba(255, 230, 0, 0.15) 0%, rgba(255, 230, 0, 0.25) 100%)'; // MercadoPago amarillo
    }
    return 'rgba(0, 0, 0, 0.02)';
  }};
  border: 2px solid ${props => {
    if (props.$selected) {
      return props.$recommended ? '#D97D2B' : '#FFE600'; // MercadoPago amarillo
    }
    return 'rgba(0, 0, 0, 0.1)';
  }};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.3s ease;
  cursor: pointer;
  align-items: center;
  flex-wrap: balance;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props =>
    props.$recommended
      ? 'rgba(217, 125, 43, 0.2)'
      : 'rgba(255, 230, 0, 0.3)'}; // MercadoPago amarillo shadow
    border-color: ${props => props.$recommended ? '#D97D2B' : '#FFE600'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 0.65rem;
  }
`;

export const PriceInterestFreeLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PriceLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: auto;
  
  @media (max-width: 768px) {
    gap: 0.6rem;
  }
`;

export const PaymentIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${props => props.$mercadopago ? '#00AEEF' : '#D97D2B'}; // MercadoPago celeste
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;



export const PriceLabel = styled.span`
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  color: #333;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const InterestFreeLabel = styled.span`
  font-size: 0.8rem;
  color: #999;
  font-weight: 600;
`;

export const PriceWithDiscount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const PriceContainer = styled.div`
  min-height: 3rem;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    min-height: 2.8rem;
  }
`;

export const PriceAmount = styled.span`
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  color: #1E4C5C;
  font-weight: 800;
  transition: color 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const OriginalPrice = styled.span`
  font-size: 1.2rem;
  color: #999;
  text-decoration: line-through;
  font-weight: 500;
`;

export const PriceDetail = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0.5rem 0 0 0;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

export const RecommendedBadge = styled.span`
  position: absolute;
  top: -10px;
  right: 20px;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  color: white;
  padding: 0.35rem 0.9rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(217, 125, 43, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.3rem 0.75rem;
    right: 15px;
  }
`;

export const DiscountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(217, 125, 43, 0.15);
  color: #D97D2B;
  padding: 0.4rem 0.9rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 700;
  border: 1px solid rgba(217, 125, 43, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.35rem 0.75rem;
  }
`;

export const SelectedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #28a745;
  font-size: 0.95rem;
  font-weight: 600;
  
  svg {
    font-size: 1.1rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    
    svg {
      font-size: 1rem;
    }
  }
`;

export const CTAButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1.25rem 2rem;
  background: #25D366;
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.4);
  margin-bottom: 1rem;
  text-decoration: none;
  
  svg {
    font-size: 1.5rem;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(37, 211, 102, 0.6);
    background: #20BD5A;
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1.1rem 1.75rem;
    font-size: 1.1rem;
    gap: 0.6rem;
    
    svg {
      font-size: 1.3rem;
    }
  }
`;

// Alias for CTAButton to use as link
export const WhatsAppButton = styled(CTAButton).attrs({ as: 'a' })`
      max-width: 600px;
      margin: 0 auto;
      margin-bottom: 1rem;
`;

export const SecondaryLink = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  color: #1E4C5C;
  font-size: 0.9rem;
  text-decoration: none;
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: #D97D2B;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

// ========================================
// Sections
// ========================================

export const Section = styled.section`
  padding: 5rem 1.5rem;
  background: ${props => props.$background || 'white'};
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  overflow-y: visible;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 3.5rem 0.5rem;
  }
`;

export const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(1.8rem, 5vw, 2.5rem);
  color: #1E4C5C;
  margin-top: 0;
  margin-bottom: 1rem;
  font-weight: 700;
`;

// ========================================
// Features List
// ========================================

export const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(217, 125, 43, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    gap: 1rem;
  }
`;

export const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(217, 125, 43, 0.3);
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    min-width: 45px;
    font-size: 1.3rem;
  }
`;

export const FeatureText = styled.p`
  font-size: 1.05rem;
  color: #333;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

// ========================================
// Steps List
// ========================================

export const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  animation: ${fadeInUp} 0.6s ease-out;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    box-shadow: 0 8px 25px rgba(30, 76, 92, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    gap: 1.25rem;
  }
`;

export const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: linear-gradient(135deg, #1E4C5C 0%, #2A6A7E 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.8rem;
  font-weight: 800;
  box-shadow: 0 4px 15px rgba(30, 76, 92, 0.3);
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    min-width: 50px;
    font-size: 1.5rem;
  }
`;

export const StepContent = styled.div`
  flex: 1;
`;

export const StepTitle = styled.h3`
  font-size: 1.3rem;
  color: #1E4C5C;
  margin-bottom: 0rem;
  font-weight: 500;
  margin-top: 0;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

export const StepDescription = styled.p`
  font-size: 1.05rem;
  color: #666;
  margin: 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

// Alias for StepDescription
export const StepText = StepDescription;

export const StepIcon = styled.div`
  font-size: 2.5rem;
  color: #D97D2B;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// ========================================
// Swiper Gallery Styles
// ========================================

export const SwiperGalleryContainer = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  animation: ${fadeInScale} 0.6s ease-out;
  position: relative;
  overflow: visible;
  
  @media (max-width: 1024px) {
    max-width: 450px;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

export const MainSwiperWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow: visible;
  
  .main-swiper {
    width: 400px;
    height: 650px;
    border-radius: 20px;
    background: transparent;
    padding: 20px 0;
    margin: 0 auto;
    overflow: visible;
    
    @media (max-width: 768px) {
      width: 75%;
      max-width: 300px;
      height: 500px;
      border-radius: 16px;
      padding: 15px 0;
      overflow: visible;
    }
    
    @media (max-width: 480px) {
      width: 70%;
      max-width: 280px;
      height: 450px;
      padding: 10px 0;
      overflow: visible;
    }
  }
  
  .swiper-slide {
    text-align: center;
    font-size: 18px;
    background: transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100% !important;
    max-width: 100%;
    height: auto !important;
    max-height: 100%;
    box-sizing: border-box;
    border-radius: 20px !important;
    
    @media (max-width: 768px) {
      border-radius: 16px !important;
    }
  }
  
  .swiper-wrapper {
    max-width: 100%;
    align-items: center;
    overflow: visible !important;
  }
  
`;


export const SwiperImage = styled.img`
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  display: block;
  box-sizing: border-box;
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    border-radius: 16px !important;
  }
`;

// ========================================
// FAQ
// ========================================

export const FAQList = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FAQItem = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  animation: ${fadeInUp} 0.6s ease-out;
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(217, 125, 43, 0.15);
    border-color: rgba(217, 125, 43, 0.2);
  }
  
  &:active {
    transform: scale(0.99);
  }
`;

export const FAQHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.75rem 2rem;
  gap: 1rem;
  user-select: none;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem;
  }
`;

export const FAQQuestion = styled.h3`
  font-size: 1.25rem;
  color: #1E4C5C;
  margin: 0;
  font-weight: 700;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const FAQIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #D97D2B;
  font-size: 1.25rem;
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const FAQAnswer = styled.div`
  display: grid;
  grid-template-rows: ${props => props.$isOpen ? '1fr' : '0fr'};
  transition: grid-template-rows 0.4s ease;
  overflow: hidden;
`;

export const FAQAnswerContent = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 0;
  line-height: 1.6;
  min-height: 0;
  padding: ${props => props.$isOpen ? '0 2rem 1.75rem 2rem' : '0 2rem'};
  transition: padding 0.4s ease;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: ${props => props.$isOpen ? '0 1.5rem 1.5rem 1.5rem' : '0 1.5rem'};
  }
`;

// ========================================
// Footer
// ========================================

export const Footer = styled.footer`
  background: linear-gradient(135deg, #1E4C5C 0%, #143742 100%);
  color: white;
  padding: 3rem 2rem 2rem;
  text-align: center;
`;

export const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

export const SocialLink = styled.a`
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    transform: translateY(-3px) scale(1.1);
  }
  
  /* Instagram */
  &[aria-label="Instagram"]:hover {
    background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%);
    border-color: #E4405F;
    box-shadow: 0 8px 20px rgba(228, 64, 95, 0.5);
  }
  
  /* Facebook */
  &[aria-label="Facebook"]:hover {
    background: #1877F2;
    border-color: #1877F2;
    box-shadow: 0 8px 20px rgba(24, 119, 242, 0.5);
  }
  
  /* TikTok */
  &[aria-label="TikTok"]:hover {
    background: #000000;
    border-color: #FE2C55;
    box-shadow: 0 8px 20px rgba(254, 44, 85, 0.5);
  }
  
  /* WhatsApp */
  &[aria-label="WhatsApp"]:hover {
    background: #25D366;
    border-color: #25D366;
    box-shadow: 0 8px 20px rgba(37, 211, 102, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 1.3rem;
  }
`;

export const FooterText = styled.p`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;
